import React, { ChangeEvent, FC, Fragment, ReactElement, useContext, useMemo } from 'react';
import {
    OnSpotAnswer,
    OnSpotContextProps,
    OnSpotQuestions,
    OnSpotReturnTree,
    TreeSectionMapping,
} from '@/model/onSpot/OnSpot';
import { buildTreeFromArrays } from '../_helper/_helper';
import { TreeNode } from '@/model/onSpot/TreeNodes';
import { NavLink } from 'react-router';
import './onSpotReview.scss';
import { OnSpotContext } from '../index';
import Button, { ButtonProps } from '@/comp/button/button';
import ReviewContact from './components/ContactReview';
import UploadFileListReview from './components/UploadFileListReview';
import { AppContext, AppContextProps } from '@/context/AppContext';
import OnSpotApi from '@/api/onSpot/OnSpot';
import BackButton from '@/comp/backButton/backButton';
import { getAnswerFromReturnTreeAnswerId } from '@/pages/customerarea/onSpot/_helper/answerHelper';
import { PageState } from '@/model/common/PageState';
import { Icon } from '@/comp/icon/icon';

interface OnSpotReviewProps {
    data: OnSpotQuestions;
    returnTree: OnSpotReturnTree;
    treeSectionMapping: TreeSectionMapping;
    submitData: (data: { formValidated: boolean }) => void;
}

type AnswerReviewElementProps = {
    parentTreeId: string;
    node: TreeNode;
};

export const OnSpotReview = ({ treeSectionMapping, submitData }: OnSpotReviewProps) => {
    const {
        dataReturnTree,
        treeSectionId,
        displayedQuestions,
        dataTree,
        contactData,
        fileLists,
        pageState,
        setPageState,
    } = useContext(OnSpotContext) as OnSpotContextProps;
    const { bookingData } = useContext(AppContext) as AppContextProps;
    const [formErrors, setFormErrors] = React.useState(false);
    const [formValid, setFormValid] = React.useState(false);

    const submitButtonProps = useMemo<ButtonProps>(
        () => ({
            type: 'submit',
            label: pageState === PageState.Submitting ? 'Anliegen wird übermittelt' : 'Anliegen jetzt abschicken',
            validated: pageState === PageState.Submitting,
        }),
        [pageState]
    );

    const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        // set form errors if form not valid
        setFormErrors(!formValid);

        // if form is valid, send data
        if (formValid) {
            submitData({ formValidated: true });
            setPageState(PageState.Submitting);

            uploadFiles()
                .then(async () => {
                    try {
                        await uploadDataTree();
                        setPageState(PageState.Success);
                    } catch {
                        setPageState(PageState.Error);
                    } finally {
                        submitData({ formValidated: false });
                    }
                })
                .catch(() => {
                    submitData({ formValidated: false });
                    setPageState(PageState.Error);
                });
        }
    };

    const uploadFiles = async () => {
        const bookingNumber = Number(bookingData?.bookingId ?? null);
        if (bookingNumber && fileLists) {
            return OnSpotApi.saveFiles({ fileLists, bookingNumber })
                .then(() => {
                    return Promise.resolve();
                })
                .catch(() => {
                    return Promise.reject();
                });
        }

        return Promise.reject();
    };

    const uploadDataTree = async () => {
        const bookingNumber = bookingData?.bookingId ?? null;
        let countFileList = 0;
        Object.keys(fileLists).forEach((type) => {
            fileLists[type].forEach((file) => {
                if (file.file) {
                    countFileList++;
                }
            });
        });
        if (bookingNumber && dataReturnTree) {
            return OnSpotApi.saveOnSpotTree(dataReturnTree, String(bookingNumber), contactData, countFileList).then(
                (response) => {
                    return response;
                }
            );
        }
        return Promise.reject('On Spot upload failed');
    };

    const changeFormValid = (e: ChangeEvent<HTMLInputElement>) => {
        setFormValid(e.target.checked);
        setFormErrors(!e.target.checked);
    };

    const AnswerReviewElement: React.FC<AnswerReviewElementProps> = ({ node, parentTreeId }) => {
        const RecursiveQuestionElement: FC<{ questionRef: string }> = ({ questionRef }) => {
            const dataReturnQuestion = dataReturnTree[questionRef];
            const question = dataTree['question-' + dataReturnQuestion.questionId];

            return (
                <Fragment key={`question-${dataReturnQuestion.questionId}`}>
                    {dataReturnQuestion.answers.map((answer, index) => {
                        const displayAnswer: OnSpotAnswer | null = getAnswerFromReturnTreeAnswerId(dataTree, answer.id);

                        return (
                            <>
                                <div className="question-container" key={'review-question' + index}>
                                    <h4>{question.question}</h4>

                                    {(() => {
                                        if (question.type === 'singleChoice' || question.type === 'multipleChoice') {
                                            return (
                                                <div key={`answer-${node.id}-${index}`} className="answer">
                                                    {displayAnswer ? displayAnswer.label : ''}
                                                </div>
                                            );
                                        } else {
                                            return (
                                                <div key={`answer-${node.id}-${index}`} className="answer">
                                                    {answer.freeText}
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                                {answer.$refId && (
                                    <RecursiveQuestionElement key={answer.$refId} questionRef={answer.$refId} />
                                )}
                            </>
                        );
                    })}
                </Fragment>
            );
        };

        const initialQuestionId = displayedQuestions[node.treeId][0].id;

        return (
            <div className="container">
                <h2 className="color-primary">
                    <Icon name="syncProblem" size={28} ariaHidden />
                    {parentTreeId !== node.treeId ? (
                        <>
                            {treeSectionMapping[parentTreeId]} - {window.$browserInfo?.device === 'mobile' && <br />}
                            {treeSectionMapping[node.treeId]}
                        </>
                    ) : Object.keys(displayedQuestions).length === 1 ? (
                        'Nicht in gebuchter Unterkunft'
                    ) : (
                        treeSectionMapping[parentTreeId]
                    )}
                </h2>
                <RecursiveQuestionElement
                    key={`question-${initialQuestionId}`}
                    questionRef={`question-${initialQuestionId}`}
                />
            </div>
        );
    };

    const tree = (nodes: TreeNode) => {
        const elements: ReactElement<Element>[] = [];
        if (nodes) {
            if (nodes.children.length > 0) {
                nodes.children.forEach((node) => {
                    if (node.treeId !== treeSectionId) {
                        elements.push(
                            <article>
                                <AnswerReviewElement parentTreeId={nodes.treeId} key={`tree-${node.id}`} node={node} />
                            </article>
                        );
                    } else if (node.children) {
                        tree(node);
                    }
                });
            } else {
                elements.push(
                    <article>
                        <AnswerReviewElement parentTreeId={nodes.treeId} key={`tree-${nodes.id}`} node={nodes} />
                    </article>
                );
            }
        }

        return elements;
    };

    const QuestionReviewElement = () => {
        const array = Object.keys(displayedQuestions).map((key) => {
            return key;
        });
        const treeArray = buildTreeFromArrays(array);

        return (
            <>
                {treeArray &&
                    dataTree &&
                    dataReturnTree &&
                    (treeArray?.children.length > 0 ? (
                        treeArray.children.map((childArray) => tree(childArray))
                    ) : (
                        <article>
                            <div className="container">
                                <AnswerReviewElement
                                    key={`entry-${treeArray.id}`}
                                    node={treeArray}
                                    parentTreeId={treeArray.treeId}
                                />
                            </div>
                        </article>
                    ))}
            </>
        );
    };

    return (
        <>
            <div className="content-wrapper">
                <div className="intro row">
                    <h1 className="color-primary">Zusammenfassung</h1>
                    <div className="intro-text">
                        <p>
                            Fast geschafft! Hier können Sie noch einmal Ihre Angaben überprüfen, bevor Sie das Formular
                            abschicken.
                        </p>
                    </div>
                </div>
            </div>
            <section className="section-review-step">
                {displayedQuestions && <QuestionReviewElement />}
                <UploadFileListReview fileLists={fileLists} />
                <ReviewContact />
            </section>
            <section>
                <div className="container">
                    <div className="accept-sec">
                        <input
                            type="checkbox"
                            name="privacy"
                            id="privacy-checkbox"
                            checked={formValid}
                            onChange={changeFormValid}
                        />
                        <label htmlFor="privacy-checkbox">
                            Durch das Absenden Ihrer Daten sind Sie damit einverstanden, dass wir Sie zu Ihrem Anliegen
                            kontaktieren. Außerdem stimmen Sie zu, dass wir Ihre Daten nach Datenschutzrichtlinien
                            speichern und verarbeiten. Hier finden Sie unsere{' '}
                            <NavLink to="/datenschutz" target="_blank">
                                Datenschutzhinweise
                            </NavLink>{' '}
                            und{' '}
                            <a href={`${window.$website}/unternehmen/agb`} target="_blank" rel="noopener noreferrer">
                                AGB
                            </a>
                            .
                        </label>
                    </div>
                    {formErrors && (
                        <p className="error text-red">
                            Um das Formular abschicken zu können, müssen Sie die Datenschutzhinweise und Allgemeinen
                            Geschäftsbedingungen akzeptieren.
                        </p>
                    )}
                </div>
            </section>
            <section className="section-submit">
                <div className="container justify-between">
                    <BackButton to={'/hilfe-vor-ort/kontakt'} label={'Zurück zu den Kontakten'} />
                    <Button
                        type={submitButtonProps.type}
                        label={submitButtonProps.label}
                        validated={submitButtonProps.validated}
                        valid={formValid}
                        handleClick={handleSubmit}
                    />
                </div>
            </section>
        </>
    );
};
